import { useCallback, useMemo, useState } from "react";

import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import type { IScoringRuleDto } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { useTeamsListQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamsListQuery";
import type { TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

import { resolveInitialTeamIds, type ScoringRuleWizardMode } from "./resolveInitialTeamIds";
import { resolveSelectionForScope, resolveTeamScope, ScoringRuleTeamScope } from "./teamScope";

type Params = {
    mode: ScoringRuleWizardMode;
    initialRule?: IScoringRuleDto;
    defaultTeamIds: readonly number[];
    enabled: boolean;
};

export type ScoringRuleTeamScopeSelection = {
    teams: readonly TeamListItem[];
    scope: ScoringRuleTeamScope;
    selectedTeamIds: readonly number[];
    selectScope: (scope: ScoringRuleTeamScope) => void;
    toggleTeam: (teamId: number) => void;
    /** The teams to save on the rule — an account-wide rule is saved without any. */
    teamIdsForPayload: readonly number[] | undefined;
    isValid: boolean;
};

export const useScoringRuleTeamScope = ({
    mode,
    initialRule,
    defaultTeamIds,
    enabled,
}: Params): ScoringRuleTeamScopeSelection => {
    const { data: teams = EMPTY_ARRAY } = useTeamsListQuery({ enabled });

    const [selectedTeamIds, setSelectedTeamIds] = useState<readonly number[]>(() =>
        resolveInitialTeamIds(mode, initialRule, defaultTeamIds),
    );
    const [isAccountScoped, setIsAccountScoped] = useState(() => selectedTeamIds.length === 0);

    const allTeamIds = useMemo(() => teams.map((team) => team.teamId), [teams]);
    const scope = useMemo(
        () => resolveTeamScope(isAccountScoped, selectedTeamIds, allTeamIds),
        [isAccountScoped, selectedTeamIds, allTeamIds],
    );

    const selectScope = useCallback(
        (nextScope: ScoringRuleTeamScope) => {
            setIsAccountScoped(nextScope === ScoringRuleTeamScope.Account);
            setSelectedTeamIds((current) => resolveSelectionForScope(nextScope, current, allTeamIds));
        },
        [allTeamIds],
    );

    const toggleTeam = useCallback((teamId: number) => {
        setSelectedTeamIds((current) =>
            current.includes(teamId) ? current.filter((id) => id !== teamId) : [...current, teamId],
        );
    }, []);

    return {
        teams,
        scope,
        selectedTeamIds,
        selectScope,
        toggleTeam,
        teamIdsForPayload: isAccountScoped || selectedTeamIds.length === 0 ? undefined : selectedTeamIds,
        isValid: isAccountScoped || selectedTeamIds.length > 0,
    };
};
